import { useCallback } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import DiagramArrowheadAssociationLeftIcon from '@atlaskit/icon-lab/core/diagram-arrowhead-association-left';
import DiagramArrowheadAssociationRightIcon from '@atlaskit/icon-lab/core/diagram-arrowhead-association-right';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';

export type GalleryControlsSize = 'small' | 'large';

export type GalleryControlsProps = {
	showCounter?: boolean;
	currentCount?: number;
	totalCount?: number;
	onPrevious: () => void;
	onNext: () => void;
	isFirstItemVisible?: boolean;
	isLastItemVisible?: boolean;
	size?: GalleryControlsSize;
	/** `'arrow'` = Figma gallery control (default). `'chevron'` = icon-only. */
	iconAppearance?: 'arrow' | 'chevron';
};

const containerCss = css({
	display: 'inline-flex',
	flexDirection: 'row',
	alignItems: 'center',
	// 4px — Figma Gallery Control gap
	gap: token('space.050'),
	'--button-color': wacToken('color.primary.black'),
	'--hover-color': wacToken('color.secondary.navy'),
	'--active-color': wacToken('color.secondary.navy'),
	'--disabled-color': wacToken('color.primary.black'),
	'--focus-color': token('color.border.focused'),
	'--arrow-selected-bg': wacToken('color.neutral.light300'),
});

const chevronContainerCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
});

const darkThemeContainerCss = css({
	'--button-color': wacToken('color.neutral.light0'),
	'--hover-color': wacToken('color.extended.blue300'),
	'--active-color': wacToken('color.extended.blue300'),
	'--disabled-color': wacToken('color.neutral.light0'),
	'--focus-color': token('color.border.focused'),
	'--arrow-selected-bg': wacToken('color.neutral.light800'),
});

const buttonBaseCss = css({
	display: 'inline-flex',
	cursor: 'pointer',
	flexShrink: 0,
	border: 'none',
	borderRadius: '100%',
	flexDirection: 'column',
	opacity: 1,
	width: '40px',
	height: '40px',
	'&:active:not(:disabled)': {
		transform: 'scale(0.97)',
	},
	'&:disabled': {
		opacity: 0.3,
		cursor: 'default',
	},
	'&:focus-visible:not(:disabled)': {
		outline: '2px solid var(--focus-color)',
		outlineOffset: '2px',
	},
});

// Figma: Gallery Controls Arrow — 51px, base = icon only, hover = 2px ring, selected = fill + ring
const arrowButtonCss = css({
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	width: '51px',
	height: '51px',
	padding: 0,
	backgroundColor: 'transparent',
	color: 'var(--button-color)',
	border: '2px solid transparent',
	// The @atlaskit/icon-lab arrow glyphs render at a fixed 16px via the new icon
	// engine (no public size prop reaches 24px), so size the rendered svg directly.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& svg': {
		width: '24px',
		height: '24px',
	},
	'&:hover:not(:disabled)': {
		borderColor: 'var(--button-color)',
	},
	'&:active:not(:disabled)': {
		backgroundColor: 'var(--arrow-selected-bg)',
		borderColor: 'var(--button-color)',
		transform: 'none',
	},
	'&:disabled': {
		opacity: 0.3,
	},
});

const chevronButtonCss = css({
	alignItems: 'center',
	justifyContent: 'center',
	padding: 0,
	backgroundColor: 'transparent',
	color: 'var(--button-color)',
	'&:hover:not(:disabled)': { color: 'var(--hover-color)' },
	'&:active:not(:disabled)': { color: 'var(--active-color)' },
	'&:disabled': { color: 'var(--disabled-color)' },
});

const buttonLargeCss = css({
	[media.above.md]: {
		width: '60px',
		height: '60px',
	},
});

const counterCss = css({
	minWidth: '40px',
	textAlign: 'center',
	userSelect: 'none',
	margin: 0,
});

type GalleryNavButtonProps = {
	direction: 'previous' | 'next';
	isChevron: boolean;
	isLarge: boolean;
	disabled: boolean;
	onClick: () => void;
	ariaLabel: string;
	testId: string;
};

const getNavIcon = (direction: 'previous' | 'next', isChevron: boolean) => {
	if (isChevron) {
		return direction === 'previous' ? (
			<ChevronLeftIcon label="" spacing="none" size="small" />
		) : (
			<ChevronRightIcon label="" spacing="none" size="small" />
		);
	}
	return direction === 'previous' ? (
		<DiagramArrowheadAssociationLeftIcon label="" spacing="none" />
	) : (
		<DiagramArrowheadAssociationRightIcon label="" spacing="none" />
	);
};

const GalleryNavButton = ({
	direction,
	isChevron,
	isLarge,
	disabled,
	onClick,
	ariaLabel,
	testId,
}: GalleryNavButtonProps) => (
	<button
		type="button"
		css={[
			buttonBaseCss,
			isChevron ? chevronButtonCss : arrowButtonCss,
			isLarge && isChevron && buttonLargeCss,
		]}
		onClick={onClick}
		disabled={disabled}
		aria-label={ariaLabel}
		data-testid={testId}
	>
		{getNavIcon(direction, isChevron)}
	</button>
);

export const GalleryControls = ({
	showCounter = false,
	currentCount,
	totalCount,
	onPrevious,
	onNext,
	isFirstItemVisible,
	isLastItemVisible,
	size,
	iconAppearance = 'arrow',
}: GalleryControlsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { isDarkTheme } = useTheme();

	const fireGalleryControlClicked = useCallback(
		(direction: 'previous' | 'next') => {
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'galleryControlButton',
				eventType: 'ui',
				eventComponent: 'button',
				source: 'gallery-controls',
				attributes: {
					direction,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handlePreviousClick = useCallback(() => {
		fireGalleryControlClicked('previous');
		onPrevious();
	}, [fireGalleryControlClicked, onPrevious]);

	const handleNextClick = useCallback(() => {
		fireGalleryControlClicked('next');
		onNext();
	}, [fireGalleryControlClicked, onNext]);

	const isLarge = size === 'large';
	const counterTypographyClass = size === 'small' ? 'body-sm' : 'body-md';
	const isAtFirstItem = currentCount !== undefined && currentCount < 2;
	const isAtLastItem =
		totalCount !== undefined && currentCount !== undefined && currentCount >= totalCount;
	const isChevron = iconAppearance === 'chevron';

	return (
		<div
			css={[containerCss, isDarkTheme && darkThemeContainerCss, isChevron && chevronContainerCss]}
			role="group"
			aria-label={intl.formatMessage({
				id: 'gallery-controls.group-label',
				defaultMessage: 'Gallery navigation',
			})}
		>
			<GalleryNavButton
				direction="previous"
				isChevron={isChevron}
				isLarge={isLarge}
				disabled={Boolean(isFirstItemVisible || isAtFirstItem)}
				onClick={handlePreviousClick}
				ariaLabel={intl.formatMessage({
					id: 'gallery-controls.previous',
					defaultMessage: 'Previous slide',
				})}
				testId="gallery-controls-previous"
			/>
			{showCounter && (
				<p
					css={counterCss}
					className={counterTypographyClass}
					role="status"
					aria-live="polite"
					aria-atomic="true"
					data-testid="gallery-controls-counter"
					aria-label={intl.formatMessage(
						{
							id: 'gallery-controls.counter',
							defaultMessage: '{current} of {total} slide',
						},
						{
							current: currentCount,
							total: totalCount,
						},
					)}
				>
					{currentCount}/{totalCount}
				</p>
			)}
			<GalleryNavButton
				direction="next"
				isChevron={isChevron}
				isLarge={isLarge}
				disabled={Boolean(isLastItemVisible || isAtLastItem)}
				onClick={handleNextClick}
				ariaLabel={intl.formatMessage({
					id: 'gallery-controls.next',
					defaultMessage: 'Next slide',
				})}
				testId="gallery-controls-next"
			/>
		</div>
	);
};
